'use client'

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { resolveRouteSkeleton } from '@/components/skeletons/RouteLoading'
import { registerNavigationPendingHandler } from '@/lib/navigation-pending'

const SHOW_DELAY_MS = 40
const SAFETY_MS = 10_000

function normalizePath(path: string): string {
  if (!path) return '/'
  const bare = path.split('?')[0]?.split('#')[0] || '/'
  if (bare.length > 1 && bare.endsWith('/')) return bare.slice(0, -1)
  return bare
}

function currentKey(pathname: string, search: string): string {
  const p = normalizePath(pathname)
  return search ? `${p}?${search}` : p
}

function hrefKey(href: string): string | null {
  try {
    const url = new URL(href, window.location.origin)
    if (url.origin !== window.location.origin) return null
    const p = normalizePath(url.pathname)
    return url.search ? `${p}${url.search}` : p
  } catch {
    return null
  }
}

/**
 * Dahili navigasyonda hedef path skeleton’ı.
 * URL değişince (redirect dahil) kapanır; skeleton sırasında children unmount (medya kesilir).
 */
export default function NavigationSkeletonGate({
  children,
}: {
  children: ReactNode
}) {
  const pathname = usePathname() || '/'
  const searchParams = useSearchParams()
  const search = searchParams?.toString() || ''
  const urlKey = currentKey(pathname, search)

  const [pendingKey, setPendingKey] = useState<string | null>(null)
  const [skeletonPath, setSkeletonPath] = useState<string | null>(null)
  const [visible, setVisible] = useState(false)
  const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const safetyTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fromKeyRef = useRef<string | null>(null)

  const clearTimers = () => {
    if (showTimer.current) clearTimeout(showTimer.current)
    if (safetyTimer.current) clearTimeout(safetyTimer.current)
    showTimer.current = null
    safetyTimer.current = null
  }

  const clearPending = () => {
    clearTimers()
    fromKeyRef.current = null
    setPendingKey(null)
    setSkeletonPath(null)
    setVisible(false)
  }

  const startPending = (key: string) => {
    if (key === urlKey) return
    clearTimers()
    fromKeyRef.current = urlKey
    setPendingKey(key)
    setSkeletonPath(normalizePath(key.split('?')[0] || '/'))
    setVisible(false)
    showTimer.current = setTimeout(() => setVisible(true), SHOW_DELAY_MS)
    safetyTimer.current = setTimeout(clearPending, SAFETY_MS)
  }

  // Herhangi bir URL değişimi = navigasyon bitti (alias redirect dahil)
  useEffect(() => {
    if (!pendingKey) return
    if (fromKeyRef.current !== null && urlKey !== fromKeyRef.current) {
      clearPending()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey, pendingKey])

  useEffect(() => {
    registerNavigationPendingHandler(startPending)
    return () => registerNavigationPendingHandler(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey])

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented) return
      if (event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

      const el = event.target as Element | null
      const anchor = el?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!anchor) return
      if (anchor.hasAttribute('download')) return
      if (anchor.target && anchor.target !== '_self') return
      const rel = anchor.getAttribute('rel') || ''
      if (rel.includes('external')) return

      const rawHref = anchor.getAttribute('href') || ''
      if (rawHref.startsWith('#')) return

      const key = hrefKey(rawHref)
      if (!key) return
      startPending(key)
    }

    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey])

  useEffect(() => () => clearTimers(), [])

  const showSkeleton = Boolean(pendingKey && visible && skeletonPath)

  if (showSkeleton) {
    return (
      <div className="min-w-0" aria-live="polite">
        {resolveRouteSkeleton(skeletonPath)}
      </div>
    )
  }

  return <div className="min-w-0">{children}</div>
}
